import React, { useState, useEffect } from 'react';
import { EyeOff, ZoomIn, ZoomOut, RefreshCw, Accessibility } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

export const AccessibilityButton: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState({
    highContrast: false,
    fontSize: 100,
    grayscale: false,
    underlineLinks: false,
    readableFont: false,
  });

  useEffect(() => {
    const saved = localStorage.getItem('accessibility-settings');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSettings(parsed);
        applySettings(parsed);
      } catch (e) {

      }
    }
  }, []);

  const applySettings = (newSettings: typeof settings) => {
    if (newSettings.highContrast) {
      document.documentElement.classList.add('accessibility-high-contrast');
    } else {
      document.documentElement.classList.remove('accessibility-high-contrast');
    }

    if (newSettings.grayscale) {
      document.documentElement.classList.add('accessibility-grayscale');
    } else {
      document.documentElement.classList.remove('accessibility-grayscale');
    }

    if (newSettings.underlineLinks) {
      document.documentElement.classList.add('accessibility-underline-links');
    } else {
      document.documentElement.classList.remove('accessibility-underline-links');
    }

    if (newSettings.readableFont) {
      document.documentElement.classList.add('accessibility-readable-font');
    } else {
      document.documentElement.classList.remove('accessibility-readable-font');
    }

    document.documentElement.style.fontSize = `${newSettings.fontSize}%`;
  };

  const updateSetting = (key: keyof typeof settings, value: any) => {
    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);
    applySettings(newSettings);
    localStorage.setItem('accessibility-settings', JSON.stringify(newSettings));
  };

  const resetSettings = () => {
    const defaultSettings = {
      highContrast: false,
      fontSize: 100,
      grayscale: false,
      underlineLinks: false,
      readableFont: false,
    };
    setSettings(defaultSettings);
    applySettings(defaultSettings);
    localStorage.setItem('accessibility-settings', JSON.stringify(defaultSettings));
  };

  const increaseFontSize = () => {
    if (settings.fontSize < 150) {
      updateSetting('fontSize', settings.fontSize + 10);
    }
  };

  const decreaseFontSize = () => {
    if (settings.fontSize > 80) {
      updateSetting('fontSize', settings.fontSize - 10);
    }
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-6 left-4 z-[9999] p-3 bg-cyan-400 text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-110"
        aria-label="תפריט נגישות"
        title="תפריט נגישות"
      >
        <Accessibility className="w-5 h-5" />
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 z-[9998]"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className="fixed bottom-20 left-4 z-[9999] bg-white rounded-2xl shadow-2xl p-6 w-80"
              style={{ direction: 'rtl' }}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900">תפריט נגישות</h3>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                  aria-label="סגור"
                >
                  <EyeOff className="w-5 h-5 text-gray-600" />
                </button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    ניגודיות גבוהה
                  </label>
                  <button
                    onClick={() => updateSetting('highContrast', !settings.highContrast)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.highContrast ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    role="switch"
                    aria-checked={settings.highContrast}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.highContrast ? 'translate-x-1' : 'translate-x-6'
                      }`}
                    />
                  </button>
                </div>

                <div>
                  <label className="text-sm font-medium text-gray-700 mb-2 block">
                    גודל גופן: {settings.fontSize}%
                  </label>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={decreaseFontSize}
                      disabled={settings.fontSize <= 80}
                      className="p-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                      aria-label="הקטן גופן"
                    >
                      <ZoomOut className="w-4 h-4" />
                    </button>
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full transition-all"
                        style={{ width: `${((settings.fontSize - 80) / 70) * 100}%` }}
                      />
                    </div>
                    <button
                      onClick={increaseFontSize}
                      disabled={settings.fontSize >= 150}
                      className="p-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
                      aria-label="הגדל גופן"
                    >
                      <ZoomIn className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    גוונים אפורים
                  </label>
                  <button
                    onClick={() => updateSetting('grayscale', !settings.grayscale)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.grayscale ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    role="switch"
                    aria-checked={settings.grayscale}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.grayscale ? 'translate-x-1' : 'translate-x-6'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    הדגש קישורים
                  </label>
                  <button
                    onClick={() => updateSetting('underlineLinks', !settings.underlineLinks)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.underlineLinks ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    role="switch"
                    aria-checked={settings.underlineLinks}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.underlineLinks ? 'translate-x-1' : 'translate-x-6'
                      }`}
                    />
                  </button>
                </div>

                <div className="flex items-center justify-between">
                  <label className="text-sm font-medium text-gray-700">
                    גופן קריא
                  </label>
                  <button
                    onClick={() => updateSetting('readableFont', !settings.readableFont)}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      settings.readableFont ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                    role="switch"
                    aria-checked={settings.readableFont}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        settings.readableFont ? 'translate-x-1' : 'translate-x-6'
                      }`}
                    />
                  </button>
                </div>

                <button
                  onClick={resetSettings}
                  className="w-full mt-4 p-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  איפוס הגדרות
                </button>
              </div>

              <p className="text-xs text-gray-500 mt-4 text-center">
                מבוסס על WCAG 2.0
              </p>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <style>{`
        .accessibility-high-contrast {
          filter: contrast(1.5) !important;
        }

        .accessibility-high-contrast * {
          border-color: #000 !important;
        }

        .accessibility-grayscale {
          filter: grayscale(100%) !important;
        }

        .accessibility-underline-links a {
          text-decoration: underline !important;
        }

        .accessibility-readable-font,
        .accessibility-readable-font * {
          font-family: Arial, sans-serif !important;
          letter-spacing: 0.05em !important;
          line-height: 1.6 !important;
        }
      `}</style>
    </>
  );
};
